import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Input, Button, Spinner } from "reactstrap";
import { useHistory } from "react-router-dom";
import {
  TbSearch,
  TbClock,
  TbCheck,
  TbX,
  TbAlertTriangle,
  TbRefresh,
  TbEye,
  TbChartBar,
  TbActivity,
  TbCube,
  TbTarget,
  TbCalendar,
  TbCheckbox
} from "react-icons/tb";
import { getContactsFilter, getAgentDashboard } from "../../../helpers/backend";
import "./AgentDashboard.css";

const AgentDashboard = () => {
  const history = useHistory();

  // Estados para casos
  const [casosStats, setCasosStats] = useState({
    ASIGNADO: 0,
    SIN_ASIGNAR: 0,
    PENDIENTE: 0,
    EN_PAUSA: 0,
    EN_PROCESO: 0,
    FINALIZADO: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [userName, setUserName] = useState("");

  // Estados para búsqueda de contactos
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Estado M3 gratis (solicitudes)
  const [m3Data, setM3Data] = useState(null);
  const [loadingM3, setLoadingM3] = useState(true);
  const [m3Error, setM3Error] = useState(null);

  // Paleta y helpers locales (sección M3)
  const pastel = {
    blue: '#9ec5fe',
    green: '#b9e6a1',
    orange: '#ffd8a8',
    purple: '#d8b4fe',
    pink: '#f5c2e7',
    teal: '#a3e3da',
    yellow: '#fff3b0',
    red: '#fecaca',
    darkText: '#334155'
  };

  const hexToRgba = (hex, alpha = 1) => {
    const h = hex.replace('#', '');
    const bigint = parseInt(h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getPctColorHex = (pct) => {
    if (pct >= 90) return pastel.red; // alto
    if (pct >= 70) return pastel.orange; // medio
    return pastel.green; // bajo
  };

  // Cargar estadísticas y datos del usuario al montar el componente
  useEffect(() => {
    // Obtener nombre del usuario del localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.get_full_name) {
      setUserName(user.get_full_name);
    } else if (user && user.username) {
      setUserName(user.username);
    }

    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoadingStats(true);
    setLoadingM3(true);
    setM3Error(null);
    try {
      // Por defecto pedimos 'ESTE_MES' o 'TODO' según lógica de negocio.
      // Usamos las fechas del mes actual para coincidir con el dashboard visual
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const formatDate = (d) => d.toISOString().split('T')[0];

      const params = {
        fecha_inicio: formatDate(firstDay),
        fecha_fin: formatDate(lastDay),
        rango: 'CUSTOM' // O 'ESTE_MES' si el backend lo soporta nativamente sin fechas
      };

      const response = await getAgentDashboard(params);
      console.log("Respuesta del Dashboard Agente:", response);
      const data = response.data || {};

      // 1. Mapear Casos por Estado
      const statsMap = {
        ASIGNADO: 0,
        SIN_ASIGNAR: 0,
        PENDIENTE: 0,
        EN_PAUSA: 0,
        EN_PROCESO: 0,
        FINALIZADO: 0
      };

      if (data.casos_por_estado) {
        data.casos_por_estado.forEach(item => {
          if (statsMap.hasOwnProperty(item.estado)) {
            statsMap[item.estado] = item.cantidad;
          }
        });
      }
      setCasosStats(statsMap);

      // 2. Mapear Datos M3
      const m3Raw = data.contador_m3_gratis_mes || {};
      const m3Parsed = {
        solicitudes_actuales: Number(m3Raw.solicitudes_actuales || 0),
        limite_solicitudes_mensual: Number(m3Raw.limite_solicitudes_mensual || 0),
        solicitudes_disponibles: Number(m3Raw.solicitudes_disponibles || 0),
        casos_1m3: Number(m3Raw.casos_1m3 || 0),
        casos_2m3: Number(m3Raw.casos_2m3 || 0),
        m3_total_mes: Number(m3Raw.m3_total_mes || 0)
      };
      setM3Data(m3Parsed);

    } catch (error) {
      console.error("Error general al cargar datos del dashboard agente:", error);
      setM3Error('No se pudo cargar la información.');
    } finally {
      setLoadingStats(false);
      setLoadingM3(false);
    }
  };

  // Buscar contactos
  const handleSearch = async () => {
    if (!searchValue.trim()) {
      alert("Por favor ingrese un valor para buscar");
      return;
    }

    setLoadingSearch(true);
    setSearchPerformed(true);

    try {
      // Construir la query específica para el campo
      const searchQuery = `?search=${encodeURIComponent(searchValue.trim())}`;

      const response = await getContactsFilter({
        changeUrl: searchQuery
      });
      console.log(response);

      setSearchResults(response.data?.results || []);

    } catch (error) {
      console.error("Error en búsqueda:", error);
      alert("Error al realizar la búsqueda. Intente nuevamente.");
      setSearchResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  // Manejar cambios en el campo de búsqueda
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Limpiar resultados
  const clearResults = () => {
    setSearchResults([]);
    setSearchValue("");
    setSearchPerformed(false);
  };

  // Navegar al perfil del contacto
  const handleContactClick = (contactId) => {
    history.push(`/contact/${contactId}/contact_profile`);
  };

  // Navegar a casos con filtro
  const handleCaseStatusClick = (estado) => {
    // Mantener persistencia existente, solo añadir marcador de origen para que Casos.js sepa ignorar snapshot inicial
    history.push({
      pathname: '/casos',
      search: `?source=dashboard&estado=${encodeURIComponent(estado)}`,
    });
  };

  // Configuración de casos con colores profesionales (sin OT Asignadas)
  const casosConfig = [
    {
      title: "OT Sin Asignar",
      count: casosStats.SIN_ASIGNAR,
      icon: TbAlertTriangle,
      color: "sin-asignar",
      estado: "SIN_ASIGNAR",
      description: "Órdenes pendientes de asignación"
    },
    {
      title: "OT Pendientes",
      count: casosStats.PENDIENTE,
      icon: TbClock,
      color: "pendiente",
      estado: "PENDIENTE",
      description: "Órdenes en espera de atención"
    },
    {
      title: "OT En Pausa",
      count: casosStats.EN_PAUSA,
      icon: TbX,
      color: "en-pausa",
      estado: "EN_PAUSA",
      description: "Órdenes temporalmente pausadas"
    },
    {
      title: "OT En Proceso",
      count: casosStats.EN_PROCESO,
      icon: TbActivity,
      color: "en-proceso",
      estado: "EN_PROCESO",
      description: "Órdenes actualmente en ejecución"
    },
    {
      title: "OT Finalizadas",
      count: casosStats.FINALIZADO,
      icon: TbCheck,
      color: "finalizado",
      estado: "FINALIZADO",
      description: "Órdenes completadas exitosamente"
    }
  ];

  // Derivar métricas M3 gratis
  const m3SolicitudesActuales = Number(m3Data?.solicitudes_actuales ?? 0);
  const m3Limite = Number(m3Data?.limite_solicitudes_mensual ?? 0);
  const m3Disponibles = Number(m3Data?.solicitudes_disponibles ?? Math.max(m3Limite - m3SolicitudesActuales, 0));
  const m3PctUsado = m3Limite > 0 ? Math.min(100, Math.round((m3SolicitudesActuales / m3Limite) * 100)) : 0;
  const m3Casos1 = Number(m3Data?.casos_1m3 ?? 0);
  const m3Casos2 = Number(m3Data?.casos_2m3 ?? 0);
  const m3TotalMes = Number(m3Data?.m3_total_mes ?? 0);

  return (
    <div className="agent-dashboard page-content">
      <Container fluid>
        {/* Header */}
        <Row className="mb-5">
          <Col xs={12}>
            <div className="agent-dashboard__header">
              <div className="agent-dashboard__header-content">
                <div className="agent-dashboard__welcome">
                  <h1 className="agent-dashboard__title">
                    <TbChartBar className="me-3" />
                    Bienvenido{userName ? `, ${userName}` : ''}
                  </h1>
                  <p className="agent-dashboard__subtitle">
                    Panel de control - Sistema de Gestión de Órdenes de Trabajo
                  </p>
                </div>
                <Button
                  color="primary"
                  size="lg"
                  onClick={fetchDashboardData}
                  disabled={loadingStats}
                  className=""
                >
                  {loadingStats ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <TbRefresh className="me-2" />
                  )}
                  Actualizar Datos
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Estadísticas de OT */}
        <div className="agent-dashboard__stats-grid">
          {casosConfig.map((caso, index) => (
            <div
              key={index}
              className={`agent-dashboard__stat-card stat-card-${caso.color}`}
              onClick={() => handleCaseStatusClick(caso.estado)}
            >
              <div className="agent-dashboard__stat-content">
                <div className="agent-dashboard__stat-info">
                  <div className="agent-dashboard__stat-header">
                    <h4>{caso.title}</h4>
                    <div className="agent-dashboard__stat-icon">
                      <caso.icon />
                    </div>
                  </div>
                  <div className="agent-dashboard__stat-number">
                    {loadingStats ? (
                      <Spinner size="sm" />
                    ) : (
                      caso.count
                    )}
                  </div>
                  <p className="agent-dashboard__stat-description">
                    {caso.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sección Solicitudes con M3 Gratis (sin botón detalles) */}
        <Row className="mb-5">
          <Col xs={12}>
            <Card className="border-2 shadow-lg">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center">
                    <div className="p-2 rounded me-3" style={{ backgroundColor: hexToRgba(pastel.green, 0.15) }}>
                      <TbCube size={24} style={{ color: pastel.green }} />
                    </div>
                    <div>
                      <h4 className="mb-1">Solicitudes con M3 Gratis (Mes)</h4>
                      <p className="text-muted mb-0">Progreso mensual del beneficio aplicado a solicitudes</p>
                    </div>
                  </div>
                  {/* Sin botón de detalles para agentes */}
                </div>
                {m3Error && (
                  <div className="alert alert-danger py-2 mb-3">{m3Error}</div>
                )}
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold">Progreso de Solicitudes Mensuales</span>
                        <span className="badge fs-6" style={{ backgroundColor: getPctColorHex(m3PctUsado), color: pastel.darkText }}>
                          {loadingM3 ? '...' : `${m3PctUsado}% usadas`}
                        </span>
                      </div>
                      <div className="mb-2" style={{ backgroundColor: '#e5e7eb', height: '12px', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ width: `${m3PctUsado}%`, backgroundColor: getPctColorHex(m3PctUsado), height: '100%', transition: 'width 0.6s ease' }} />
                      </div>
                      <div className="d-flex justify-content-between text-sm text-muted">
                        <span>{loadingM3 ? '...' : `${m3SolicitudesActuales} usadas`}</span>
                        <span>{loadingM3 ? '...' : `${m3Disponibles} disponibles`}</span>
                        <span>{loadingM3 ? '...' : `${m3Limite} límite`}</span>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="bg-light rounded p-3">
                      <h5 className="text-center mb-3">Distribución por Tamaño</h5>
                      <Row className="text-center">
                        <Col xs={6} className="mb-2">
                          <div className="border-end">
                            <div className="h4 mb-1" style={{ color: pastel.blue }}>{loadingM3 ? '...' : m3Casos1}</div>
                            <small className="text-muted">Solicitudes 1 m³</small>
                          </div>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <div>
                            <div className="h4 mb-1" style={{ color: pastel.orange }}>{loadingM3 ? '...' : m3Casos2}</div>
                            <small className="text-muted">Solicitudes 2 m³</small>
                          </div>
                        </Col>
                      </Row>
                      <div className="mt-3 text-center">
                        <small className="text-muted">Total M3 consumidos: <span className="fw-semibold" style={{ color: pastel.purple }}>{loadingM3 ? '...' : m3TotalMes}</span> m³</small>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="mt-3 g-2">
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.blue, 0.15) }}>
                      <TbCube size={20} className="mb-1" style={{ color: pastel.blue }} />
                      <div className="h5 mb-1">{loadingM3 ? '...' : m3SolicitudesActuales}</div>
                      <small className="text-muted">Solicitudes Usadas</small>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.pink, 0.15) }}>
                      <TbTarget size={20} className="mb-1" style={{ color: pastel.teal }} />
                      <div className="h5 mb-1">{loadingM3 ? '...' : m3Limite}</div>
                      <small className="text-muted">Límite Solicitudes</small>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.green, 0.15) }}>
                      <TbCheckbox size={20} className="mb-1" style={{ color: pastel.green }} />
                      <div className="h5 mb-1">{loadingM3 ? '...' : m3Disponibles}</div>
                      <small className="text-muted">Solicitudes Disponibles</small>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 rounded text-center" style={{ backgroundColor: hexToRgba(pastel.orange, 0.15) }}>
                      <TbCalendar size={20} className="mb-1" style={{ color: pastel.orange }} />
                      <div className="h5 mb-1">{loadingM3 ? '...' : `${m3PctUsado}%`}</div>
                      <small className="text-muted">% Solicitudes Usadas</small>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Buscador de Contactos */}
        <Row>
          <Col xs={12}>
            <Card className="agent-dashboard__search-card">
              <CardBody>
                <div className="agent-dashboard__search-header">
                  <h5 className="agent-dashboard__search-title">
                    <TbSearch className="me-2" />
                    Buscar Contactos
                  </h5>
                  {searchResults.length > 0 && (
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={clearResults}
                      className="agent-dashboard__clear-btn"
                    >
                      <TbX className="me-1" />
                      Limpiar
                    </Button>
                  )}
                </div>

                {/* Campo de búsqueda unificado */}
                <Row className="mb-4">
                  <Col lg={8} md={10} className="mx-auto">
                    <div className="agent-dashboard__search-field">
                      <label className="agent-dashboard__search-label">
                        Buscar por Cuenta Contrato, Cuenta Enel o Suscriptor
                      </label>
                      <div className="agent-dashboard__search-input-group">
                        <Input
                          type="text"
                          placeholder="Ingrese cuenta contrato, cuenta Enel o nombre del suscriptor..."
                          value={searchValue}
                          onChange={handleSearchChange}
                          className="agent-dashboard__search-input"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleSearch();
                            }
                          }}
                        />
                        <Button
                          color="success"
                          disabled={loadingSearch || !searchValue.trim()}
                          onClick={handleSearch}
                          className="agent-dashboard__search-btn"
                        >
                          {loadingSearch ? (
                            <Spinner size="sm" />
                          ) : (
                            <TbSearch />
                          )}
                        </Button>
                      </div>
                      <small className="agent-dashboard__search-help">
                        💡 Puede buscar por cualquiera de los tres campos: cuenta contrato, cuenta Enel o suscriptor
                      </small>
                    </div>
                  </Col>
                </Row>

                {/* Resultados de búsqueda */}
                {searchPerformed && (
                  <div className="agent-dashboard__results">
                    {loadingSearch ? (
                      <div className="agent-dashboard__loading">
                        <Spinner color="primary" />
                        <span className="ms-2">Buscando contactos...</span>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <>
                        <div className="agent-dashboard__results-header">
                          <h6>Resultados encontrados: {searchResults.length}</h6>
                        </div>
                        <div className="agent-dashboard__results-table">
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead className="agent-dashboard__table-header">
                                <tr>
                                  <th>Nombre Completo</th>
                                  <th>Dirección</th>
                                  <th>Cuenta Contrato</th>
                                  <th>Cuenta Enel</th>
                                  <th>Suscriptor</th>
                                  <th>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {searchResults.map((contact, index) => (
                                  <tr key={contact.id || index} className="agent-dashboard__table-row">
                                    <td className="agent-dashboard__contact-name">
                                      {contact.get_full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || "Sin nombre"}
                                    </td>
                                    <td>{contact.address?.[0]?.address || "N/A"}</td>
                                    <td>{contact.cuenta_contrato_ciudad_limpia || "N/A"}</td>
                                    <td>{contact.cuenta_enel || "N/A"}</td>
                                    <td>{contact.suscriptor || "N/A"}</td>
                                    <td>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleContactClick(contact.id)}
                                        className="agent-dashboard__view-btn"
                                      >
                                        <TbEye className="me-1" />
                                        Ver Perfil
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="agent-dashboard__no-results">
                        <TbSearch size={48} className="agent-dashboard__no-results-icon" />
                        <h6>No se encontraron contactos</h6>
                        <p>Intente con otros criterios de búsqueda</p>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AgentDashboard;